import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, ShieldCheck, FileSignature, Landmark, Home, Scale, CalendarClock } from "lucide-react";
import { CONTACT, IMAGES, LENDING } from "@/data/site";
import { Reveal, SectionHead } from "@/components/site/Reveal";

const EASE = [0.22, 1, 0.36, 1];
const ICONS = { ShieldCheck, FileSignature, Landmark, Home, Scale, CalendarClock };

const LendingHero = () => (
  <section className="relative pt-36 pb-20 lg:pt-48 lg:pb-28 overflow-hidden" data-testid="lending-hero">
    <div className="pointer-events-none absolute -top-32 right-[-10%] h-[600px] w-[600px] rounded-full bg-gold/10 blur-[170px]" />
    <div className="mx-auto max-w-[1440px] px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-end">
      <div className="lg:col-span-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}>
          <Link to="/" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ivory/50 hover:text-gold transition-colors" data-testid="lending-back-link">
            <ArrowLeft size={13} /> Back to home
          </Link>
          <span className="eyebrow mt-8 flex items-center gap-4">
            <span>Private Lending</span>
            <span className="h-px w-10 bg-gold/60" />
            <span>Educational Overview</span>
          </span>
        </motion.div>
        <h1 className="mt-8 font-serif font-light text-4xl sm:text-5xl lg:text-[5.2rem] leading-[1.02] tracking-[-0.015em] text-ivory" data-testid="lending-title">
          {["Earn like the bank.", "Secured by real estate."].map((l, i) => (
            <span key={l} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
              <motion.span className={`block ${i === 1 ? "italic text-gold" : ""}`} initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ duration: 1.2, delay: 0.25 + i * 0.16, ease: EASE }}>
                {l}
              </motion.span>
            </span>
          ))}
        </h1>
      </div>
      <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.8, ease: EASE }} className="lg:col-span-4 text-ivory/65 leading-relaxed font-light">
        {LENDING.intro}
      </motion.p>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="py-24 lg:py-32 bg-ink" data-testid="lending-how">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-12 grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <SectionHead index="01" eyebrow="How It Works" title={<>You fund the deal. The property secures it.</>} />
          <Reveal delay={0.2}>
            <p className="mt-8 text-ivory/60 leading-relaxed max-w-md">{LENDING.howIntro}</p>
          </Reveal>
        </div>
      </div>
      <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
        {LENDING.earn.map((e, i) => (
          <Reveal key={e.title} delay={i * 0.1} className="border border-gold/15 bg-slateCard/40 p-7 hover:border-gold/60 transition-colors duration-400" data-testid={`lending-earn-${i}`}>
            <span className="font-mono text-xs text-gold">0{i + 1}</span>
            <h3 className="mt-5 font-serif text-2xl text-ivory">{e.title}</h3>
            <p className="mt-3 text-sm text-ivory/60 leading-relaxed">{e.body}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Security = () => (
  <section className="py-24 lg:py-32 relative overflow-hidden" data-testid="lending-security">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
        <SectionHead index="02" eyebrow="Security" title={<>What stands behind your capital.</>} className="lg:col-span-7" />
        <Reveal delay={0.2} className="lg:col-span-4 lg:col-start-9">
          <p className="text-ivory/60 leading-relaxed">{LENDING.securityIntro}</p>
        </Reveal>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {LENDING.security.map((s, i) => {
          const Icon = ICONS[s.icon];
          return (
            <Reveal key={s.title} delay={(i % 3) * 0.1} className="group border border-gold/15 p-8 hover:border-gold/60 hover:-translate-y-1 transition-[border-color,transform] duration-400" data-testid={`lending-security-${i}`}>
              <Icon size={22} strokeWidth={1.25} className="text-gold" />
              <h3 className="mt-6 font-serif text-2xl text-ivory">{s.title}</h3>
              <p className="mt-3 text-sm text-ivory/60 leading-relaxed">{s.body}</p>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="py-24 lg:py-32 bg-ink" data-testid="lending-process">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-12 grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <SectionHead index="03" eyebrow="The Process" title={<>From introduction to payoff.</>} />
        <Reveal delay={0.2} className="mt-10 relative">
          <div className="clip-frame aspect-[4/3] overflow-hidden bg-obsidian">
            <img src={IMAGES.estate} alt="Property funded through private lending" className="h-full w-full object-cover opacity-80" />
          </div>
          <div className="absolute -inset-3 border border-gold/25 translate-x-3 translate-y-3 pointer-events-none" />
        </Reveal>
      </div>
      <ol className="lg:col-span-6 lg:col-start-7 border-t border-gold/15">
        {LENDING.process.map((p, i) => (
          <motion.li
            key={p.title}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 0.8, delay: i * 0.06, ease: EASE }}
            className="chapter grid grid-cols-[4rem_1fr] gap-6 py-8 border-b border-gold/15"
            data-testid={`lending-step-${i}`}
          >
            <span className="chapter-num font-mono text-sm text-ivory/35 pt-1">0{i + 1}</span>
            <div>
              <h3 className="font-serif text-2xl text-ivory">{p.title}</h3>
              <p className="mt-3 text-ivory/60 leading-relaxed text-sm lg:text-base">{p.body}</p>
            </div>
          </motion.li>
        ))}
      </ol>
    </div>
  </section>
);

const Compare = () => (
  <section className="py-24 lg:py-32" data-testid="lending-compare">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
      <SectionHead index="04" eyebrow="Lending vs. Owning" title={<>Two ways into the same asset class.</>} className="mb-14 max-w-3xl" />
      <Reveal className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-t border-gold/20 text-left" data-testid="lending-compare-table">
          <thead>
            <tr className="eyebrow">
              <th className="py-5 pr-6 font-normal">Consideration</th>
              <th className="py-5 pr-6 font-normal text-gold">Private Lender</th>
              <th className="py-5 font-normal">Property Owner</th>
            </tr>
          </thead>
          <tbody>
            {LENDING.compare.map(([k, a, b]) => (
              <tr key={k} className="border-t border-gold/10 hover:bg-gold/[0.03] transition-colors">
                <td className="py-5 pr-6 font-serif text-xl text-ivory">{k}</td>
                <td className="py-5 pr-6 text-sm text-ivory/70">{a}</td>
                <td className="py-5 text-sm text-ivory/50">{b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Reveal>
    </div>
  </section>
);

const Risks = () => (
  <section className="py-20 lg:py-28 bg-ink" data-testid="lending-risks">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
      <SectionHead index="05" eyebrow="Risks & Disclosures" title={<>Know what you're lending into.</>} className="lg:col-span-4" />
      <Reveal delay={0.15} className="lg:col-span-7 lg:col-start-6">
        <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-6">
          {LENDING.risks.map((r) => (
            <li key={r} className="flex gap-4 text-sm text-ivory/65 leading-relaxed">
              <span className="mt-2 h-px w-5 shrink-0 bg-gold/70" /> {r}
            </li>
          ))}
        </ul>
        <p className="mt-10 text-xs leading-relaxed text-ivory/35 border-t border-gold/10 pt-6" data-testid="lending-disclaimer">{LENDING.disclaimer}</p>
      </Reveal>
    </div>
  </section>
);

const CTA = () => (
  <section className="py-28 lg:py-36 relative overflow-hidden" data-testid="lending-cta">
    <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[900px] rounded-full bg-gold/10 blur-[170px]" />
    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-center">
      <Reveal className="lg:col-span-7">
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl leading-[1.05] tracking-tight text-ivory">
          Interested in lending on a <em className="not-italic text-gold">Bossard</em> project?
        </h2>
      </Reveal>
      <Reveal delay={0.15} className="lg:col-span-4 lg:col-start-9 flex flex-col gap-5 items-start">
        <a href={CONTACT.calendly} target="_blank" rel="noreferrer" className="btn-gold group inline-flex items-center gap-3 bg-gold text-obsidian text-xs tracking-[0.2em] uppercase px-7 py-4" data-testid="lending-calendly-btn">
          Schedule an Introduction Call <ArrowUpRight size={15} />
        </a>
        <Link to="/?interest=Private%20Lending#contact" className="link-underline text-xs tracking-[0.2em] uppercase text-ivory/80 hover:text-gold transition-colors" data-testid="lending-contact-link">
          Or send a written inquiry
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function PrivateLending() {
  useEffect(() => {
    document.title = "Private Lending — Bossard Investment Group LLC";
    return () => {
      document.title = "Bossard Investment Group LLC — Real Estate Investment | DC · MD · VA";
    };
  }, []);
  return (
    <main data-testid="lending-page">
      <LendingHero />
      <HowItWorks />
      <Security />
      <Process />
      <Compare />
      <Risks />
      <CTA />
    </main>
  );
}
